<template>
  <v-dialog v-model="value" width="700" persistent>
    <v-form v-model="valid" ref="form">
      <v-card class="w-100 rounded-lg">
        <v-card-title>{{ $t('spedilink.myLinks.addList') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.name"
            :label="$t('spedilink.links.nameLabel')"
            outlined
            dense
            class="mb-2"
            :rules="nameRules"
          />
          <v-text-field
            v-model="form.description"
            :label="$t('spedilink.links.descriptionLabel')"
            outlined
            dense
            class="mb-4"
            :rules="descriptionRules"
          />

          <v-checkbox
            v-model="form.companyShare"
            :label="$t('spedilink.links.companyShare')"
            hide-details
            class="mt-0 mb-4"
          />

          <div class="subtitle-2 mb-2">{{ $t('spedilink.links.personsSection') }}</div>
          <v-autocomplete
            v-model="form.userIds"
            :items="persons"
            :loading="personsLoading"
            :search-input.sync="personsSearch"
            item-value="id"
            :item-text="personDisplayText"
            :label="$t('spedilink.links.selectPersons')"
            :placeholder="$t('spedilink.links.searchPersons')"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            hide-no-data
            :class="hasNoContractors ? 'mb-1' : 'mb-4'"
            @update:search-input="onPersonsSearch"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.firstName }} {{ item.lastName }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <div v-if="hasNoContractors" class="mb-4 caption">
            <router-link :to="{ name: 'SpediLink' }" @click.native="close()">
              {{ $t('spedilink.links.noPersonsMessage') }}
            </router-link>
          </div>

          <div class="subtitle-2 mb-2">{{ $t('spedilink.links.groupsSection') }}</div>
          <v-autocomplete
            v-model="form.ownGroupIds"
            :items="ownGroups"
            :loading="ownGroupsLoading"
            :search-input.sync="ownGroupsSearch"
            item-value="id"
            item-text="name"
            :label="$t('spedilink.links.selectOwnGroups')"
            :placeholder="$t('spedilink.links.searchGroups')"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            hide-no-data
            class="mb-4"
            @update:search-input="onOwnGroupsSearch"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.description">{{ item.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <div class="subtitle-2 mb-2">{{ $t('spedilink.links.companyGroupsSection') }}</div>
          <v-autocomplete
            v-model="form.companyGroupIds"
            :items="companyGroups"
            :loading="companyGroupsLoading"
            :search-input.sync="companyGroupsSearch"
            item-value="id"
            :item-text="companyGroupDisplayText"
            :label="$t('spedilink.links.selectCompanyGroups')"
            :placeholder="$t('spedilink.links.searchGroups')"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            hide-no-data
            class="mb-2"
            @update:search-input="onCompanyGroupsSearch"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatGroupOwner(item) }}
                  <span v-if="item.description"> — {{ item.description }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions class="d-flex justify-end px-4 pb-4">
          <v-btn outlined :disabled="loading" @click="close()">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            class="ml-2"
            :loading="loading"
            :disabled="loading"
            depressed
            @click="save()"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import Service from '@/services'

export default {
  name: 'CreateSpedilinkLinkDialog',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    type: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      valid: true,
      loading: false,
      form: {
        name: '',
        description: '',
        userIds: [],
        ownGroupIds: [],
        companyGroupIds: [],
        companyShare: false
      },
      persons: [],
      personsLoading: false,
      personsSearch: '',
      personsSearchTimeout: null,
      hasNoContractors: false,
      ownGroups: [],
      ownGroupsLoading: false,
      ownGroupsSearch: '',
      ownGroupsSearchTimeout: null,
      companyGroups: [],
      companyGroupsLoading: false,
      companyGroupsSearch: '',
      companyGroupsSearchTimeout: null,
      nameRules: [
        (v) => !!v || this.$t('spedilink.links.nameRequired'),
        (v) => !v || v.length <= 200 || this.$t('spedilink.links.nameTooLong')
      ],
      descriptionRules: [
        (v) => !v || v.length <= 1000 || this.$t('spedilink.links.descriptionTooLong')
      ]
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.hasNoContractors = false
        this.checkHasContractors()
        this.loadPersons()
        this.loadOwnGroups()
        this.loadCompanyGroups()
      } else {
        this.resetForm()
      }
    }
  },
  methods: {
    personDisplayText (item) {
      return `${item.firstName} ${item.lastName} (${item.email})`
    },
    onPersonsSearch (val) {
      clearTimeout(this.personsSearchTimeout)
      this.personsSearchTimeout = setTimeout(() => {
        this.loadPersons(val)
      }, 400)
    },
    companyGroupDisplayText (item) {
      const owner = this.formatGroupOwner(item)
      return owner ? `${item.name} (${owner})` : item.name
    },
    formatGroupOwner (group) {
      const first = group.ownerFirstName || ''
      const last = group.ownerLastName || ''
      return `${first} ${last}`.trim()
    },
    onOwnGroupsSearch (val) {
      clearTimeout(this.ownGroupsSearchTimeout)
      this.ownGroupsSearchTimeout = setTimeout(() => {
        this.loadOwnGroups(val)
      }, 400)
    },
    onCompanyGroupsSearch (val) {
      clearTimeout(this.companyGroupsSearchTimeout)
      this.companyGroupsSearchTimeout = setTimeout(() => {
        this.loadCompanyGroups(val)
      }, 400)
    },
    async checkHasContractors () {
      try {
        const { data } = await Service.get('spedilink/SpediLinkUsers/GetList', { page: 1, pageSize: 1 })
        this.hasNoContractors = data.count === 0
      } catch (e) {
        this.hasNoContractors = false
      }
    },
    async loadPersons (search) {
      this.personsLoading = true
      try {
        const params = { page: 1, pageSize: 50 }
        if (search) params.search = search
        const { data } = await Service.get('spedilink/SpediLinkUsers/GetList', params)
        this.persons = data.results
      } catch (e) {
      } finally {
        this.personsLoading = false
      }
    },
    async loadOwnGroups (search) {
      this.ownGroupsLoading = true
      try {
        const params = { page: 1, pageSize: 50, scope: 'own' }
        if (search) params.search = search
        const { data } = await Service.get('spedilink/SpediLinkGroups/GetList', params)
        this.ownGroups = data.results
      } catch (e) {
      } finally {
        this.ownGroupsLoading = false
      }
    },
    async loadCompanyGroups (search) {
      this.companyGroupsLoading = true
      try {
        const params = { page: 1, pageSize: 50, scope: 'company' }
        if (search) params.search = search
        const { data } = await Service.get('spedilink/SpediLinkGroups/GetList', params)
        this.companyGroups = data.results
      } catch (e) {
      } finally {
        this.companyGroupsLoading = false
      }
    },
    close () {
      this.$emit('close')
      this.resetForm()
    },
    resetForm () {
      this.form = { name: '', description: '', userIds: [], ownGroupIds: [], companyGroupIds: [], companyShare: false }
      this.persons = []
      this.ownGroups = []
      this.companyGroups = []
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    async save () {
      this.$refs.form.validate()
      if (!this.valid) return

      this.loading = true
      try {
        await Service.post('spedilink/SpediLinks/Create', {
          name: this.form.name,
          description: this.form.description || '',
          userIds: this.form.userIds,
          groupIds: [...this.form.ownGroupIds, ...this.form.companyGroupIds],
          type: this.type,
          companyShare: this.form.companyShare
        })
        await this.$store.dispatch('setSuccessNotification', this.$t('common.confirmOperation'))
        this.$emit('link-created')
        this.resetForm()
      } catch (e) {
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
